import { MarketplacePartnerSupportHolidayFrequency } from '@atlassian/mpac-types/graphql-gateway-types';
import { getISODay, isSameDay, isWithinInterval, parse, parseISO, setYear } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { SupportAvailability } from '../types';
/**
 * Given a date & a list of holidays **in the same timezone**, returns true if the date falls on one of
 * the specified holidays.
 * **Note**: Both date & holidays are assumed to be in the same timezone.
 */
export const isSupportHoliday = options => {
  if (!options.holidays.length) return false;
  const {
    holidays,
    date
  } = options;
  return holidays.some(holiday => {
    const holidayDate = parseISO(holiday.date);
    if (holiday.holidayFrequency === MarketplacePartnerSupportHolidayFrequency.OneTime) {
      return isSameDay(holidayDate, date);
    } else if (holiday.holidayFrequency === MarketplacePartnerSupportHolidayFrequency.Annual) {
      const holidayInCurrentYear = setYear(holidayDate, date.getFullYear());
      return isSameDay(holidayInCurrentYear, date);
    }
    return false;
  });
};

/**
 * Compares the local time with partner's provided availability and returns one of:-
 * - `SupportAvailability.Open` - when we're currently within partner's support hours
 * - `SupportAvailability.Holiday` - when local time is on a support holiday
 * - `SupportAvailability.Closed` - for all other cases
 */
export const getPartnerSupportAvailability = options => {
  const {
    localTime,
    availability: {
      timezoneOffset,
      startTime,
      endTime,
      isAvailable24Hours,
      daysOfWeek,
      holidays
    }
  } = options;

  /*
    NOTE: this date object has the partner's date & time values but the timezone would be incorrect
   - would be the local timezone - and hence should not be used for further _conversions_
  */
  const currentTimeInPartnerTimezone = utcToZonedTime(localTime, timezoneOffset);
  if (isSupportHoliday({
    date: currentTimeInPartnerTimezone,
    holidays
  })) {
    return SupportAvailability.OnHoliday;
  }
  const currentDayOfWeek = getISODay(currentTimeInPartnerTimezone);
  const available24Hours = isAvailable24Hours || startTime === endTime;
  const open = daysOfWeek.some(day => {
    if (day !== currentDayOfWeek) return false;
    if (!startTime || !endTime || available24Hours) return true;

    // on the same day, now we check for time
    const startDateTime = parse(startTime, 'HH:mm', currentTimeInPartnerTimezone);
    const endDateTime = parse(endTime, 'HH:mm', currentTimeInPartnerTimezone);
    return isWithinInterval(currentTimeInPartnerTimezone, {
      start: startDateTime,
      end: endDateTime
    });
  });
  return open ? SupportAvailability.Open : SupportAvailability.Closed;
};